import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, BookOpen, FileText } from 'lucide-react';
import { getQuestions } from '../utils/questionAPI';
import { getExamById } from '../utils/examAPI';
import { getSubjectsByExamId } from '../utils/subjectsAPI';
import TabBar from '../components/TabBar';
import Header from '../components/Header';

function ExamSubjects() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [examInfo, setExamInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questionCounts, setQuestionCounts] = useState({});
  const [totalQuestionCount, setTotalQuestionCount] = useState(0);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setLoading(true);
        
        // 获取考试信息 - 使用API函数替代直接数据库操作
        const examData = await getExamById(examId);
        if (!examData) throw new Error("找不到考试信息");
        setExamInfo(examData);
        
        // 获取该考试下的所有科目 - 使用API函数替代直接数据库操作
        const subjectsData = await getSubjectsByExamId(examId);
        setSubjects(subjectsData || []);
        
        // 查询每个科目下的题目数量
        const counts = {};
        let totalCount = 0;
        for (const subject of subjectsData || []) {
          // 使用题目API获取题目数量
          const questions = await getQuestions({ examId: subject._id });
          const count = questions.length;
          counts[subject._id] = count;
          totalCount += count;
        }
        setQuestionCounts(counts);
        setTotalQuestionCount(totalCount);
        
      } catch (error) {
        console.error('Error fetching subjects:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubjects();
  }, [examId]);

  return (
    <div className="pb-24">
      <Header 
        title={examInfo?.name || '科目选择'} 
        showBack={true} 
        onBack={() => navigate('/question-bank')} 
      />
      
      <div className="p-4">
        {examInfo && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <h3 className="font-semibold text-lg text-gray-800 mb-2">{examInfo.name}</h3>
            <p className="text-gray-600 text-sm mb-3">
              {examInfo.description || '通过题库练习，熟悉考试题型和内容，提高通过率。'}
            </p>
            <div className="flex text-sm text-gray-500 space-x-4">
              <div className="flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                <span>{totalQuestionCount || examInfo.count || 0}题</span>
              </div>
            </div>
          </div>
        )}
        
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <BookOpen className="w-5 h-5 text-primary-dark mr-2" />
          选择科目
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {subjects.length > 0 ? (
              subjects.map((subject) => (
                <Link
                  key={subject._id}
                  to={`/question-practice/${subject._id}`}
                  className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-purple-100 text-primary flex items-center justify-center mr-4">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{subject.name}</h3>
                      <div className="flex text-sm text-gray-500 mt-1 space-x-3">
                        <div className="flex items-center">
                          <FileText className="w-3 h-3 mr-1" />
                          <span>{questionCounts[subject._id] ?? 0}题</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p>暂无科目数据</p>
                <button 
                  onClick={() => navigate('/question-bank')}
                  className="mt-4 px-4 py-2 bg-primary text-white rounded-full text-sm"
                >
                  返回选择其他考试
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      <TabBar />
    </div>
  );
}

export default ExamSubjects; 